// Tools for changing values in the source as a program runs with Live on
// (docs/ONLINE_IDE_LIVE_CODE.md, Phase B):
//
// - Holding Alt and dragging a number left or right changes it, by one step
//   for every few pixels, or ten with Shift held. A number with a point steps
//   in its last decimal place. Numbers stop at zero; a minus sign before one
//   stays as it is.
// - A colour swatch before the red, green and blue of a command that takes
//   them, such as Color or EntityColor, opens the browser's colour picker,
//   which changes the three numbers.
//
// Both edit the text as typing does, and call onLiveEdit(), so that Live can
// change the running program at once rather than after the pause it waits
// for after typing. The steps of a drag or a pick are kept out of the undo
// history, and the whole drag or pick goes into it as one change when it
// ends, so one undo takes it back.

// Records a drag's or a pick's change as one step in the undo history: the
// text goes back without a record (back, the changes that undo it), then
// forward with one (forward(), the changes that redo it).
function recordOnce(view, back, forward, userEvent) {
  view.dispatch({ changes: back, annotations: Transaction.addToHistory.of(false) });
  view.dispatch({ changes: forward(), userEvent, annotations: isolateHistory.of("full") });
}

import { EditorView, ViewPlugin, Decoration, WidgetType } from "@codemirror/view";
import { Prec, RangeSetBuilder, Transaction } from "@codemirror/state";
import { isolateHistory } from "@codemirror/commands";

const PIXELS_PER_STEP = 4;
// A number the compiler reads as one (compiler/toker.cpp), but not %binary.
const NUMBER = /\$[0-9a-fA-F]+|\d+\.\d*|\.\d+|\d+/g;

// Whether a place in a line is code: not in a comment or a string.
function inCode(text, offset) {
  let inString = false;
  for (let k = 0; k < offset; ++k) {
    if (text[k] === '"') inString = !inString;
    else if (text[k] === ";" && !inString) return false;
  }
  return !inString;
}

// The number at pos, as { from, to, text }, or null.
function numberAt(state, pos) {
  const line = state.doc.lineAt(pos);
  NUMBER.lastIndex = 0;
  for (let match; (match = NUMBER.exec(line.text)); ) {
    const from = line.from + match.index, to = from + match[0].length;
    if (from > pos) break;
    if (pos > to) continue;
    // Not the end of a name, such as x1, nor binary.
    const before = line.text[match.index - 1];
    if (before && /[A-Za-z0-9_%$#.]/.test(before)) return null;
    return inCode(line.text, match.index) ? { from, to, text: match[0] } : null;
  }
  return null;
}

// How a number's text steps: its value, its step, and how a value is written.
function stepper(text) {
  if (text[0] === "$") {
    const digits = text.length - 1, lower = /[a-f]/.test(text);
    return { value: parseInt(text.slice(1), 16), step: 1, format: (value) => {
      const hex = Math.round(value).toString(16).padStart(digits, "0");
      return "$" + (lower ? hex : hex.toUpperCase());
    } };
  }
  const point = text.indexOf(".");
  if (point >= 0) {
    const decimals = Math.max(1, text.length - point - 1);
    return { value: parseFloat(text), step: 10 ** -decimals, format: (value) => value.toFixed(decimals) };
  }
  return { value: parseInt(text, 10), step: 1, format: (value) => String(Math.round(value)) };
}

// Alt and a drag on a number changes it.
function scrubbing(onLiveEdit) {
  return Prec.highest(EditorView.domEventHandlers({
    mousedown(event, view) {
      if (!event.altKey || event.button !== 0) return false;
      const pos = view.posAtCoords({ x: event.clientX, y: event.clientY });
      const number = pos == null ? null : numberAt(view.state, pos);
      // Elsewhere, Alt and a drag selects a rectangle.
      if (!number) return false;
      event.preventDefault();
      const { value, step, format } = stepper(number.text);
      const startX = event.clientX, from = number.from;
      let to = number.to, last = number.text;
      document.body.classList.add("b3d-scrubbing");
      const move = (moved) => {
        const steps = Math.round((moved.clientX - startX) / PIXELS_PER_STEP) * (moved.shiftKey ? 10 : 1);
        const text = format(Math.max(0, value + steps * step));
        if (text === last) return;
        view.dispatch({ changes: { from, to, insert: text }, selection: { anchor: from + text.length },
          annotations: Transaction.addToHistory.of(false) });
        to = from + text.length;
        last = text;
        onLiveEdit();
      };
      const up = () => {
        removeEventListener("mousemove", move);
        removeEventListener("mouseup", up);
        document.body.classList.remove("b3d-scrubbing");
        if (last !== number.text) {
          recordOnce(view, { from, to, insert: number.text },
            () => ({ from, to: from + number.text.length, insert: last }), "input.scrub");
        }
      };
      addEventListener("mousemove", move);
      addEventListener("mouseup", up);
      return true;
    },
    // Alt over a number shows that it can be dragged.
    mousemove(event, view) {
      const pos = event.altKey ? view.posAtCoords({ x: event.clientX, y: event.clientY }) : null;
      view.contentDOM.classList.toggle("b3d-scrub-ready", pos != null && !!numberAt(view.state, pos));
      return false;
    },
  }));
}

// The arguments of each call on a line of text: [{ command, args: [{ from,
// to }] }], from and to being offsets in the line.
function callsIn(text, commands) {
  const calls = [];
  const word = /[A-Za-z_]\w*/g;
  for (let match; (match = word.exec(text)); ) {
    if (!inCode(text, match.index)) continue;
    const command = commands.get(match[0].toLowerCase());
    if (!command) continue;
    let k = match.index + match[0].length;
    while (text[k] === " " || text[k] === "\t") ++k;
    const bracketed = text[k] === "(";
    if (bracketed) ++k;
    const args = [];
    let depth = 0, start = k;
    for (; k <= text.length; ++k) {
      const ch = text[k];
      if (ch === '"') {
        const end = text.indexOf('"', k + 1);
        k = end < 0 ? text.length : end;
        continue;
      }
      if (ch === "(") ++depth;
      else if (ch === ")" && depth > 0) --depth;
      else if (depth === 0 && (ch === "," || ch === ")" || ch === ":" || ch === ";" || ch === undefined)) {
        args.push({ from: start, to: k });
        if (ch !== ",") break;
        start = k + 1;
      }
    }
    if (!bracketed || text[k] === ")") calls.push({ command, args });
  }
  return calls;
}

// The colours on a line: [{ rgb: [r, g, b], args: [{ from, to }] x 3 }] for
// each call of a command with red, green and blue parameters given as plain
// numbers from 0 to 255.
function coloursIn(text, commands) {
  const colours = [];
  for (const { command, args } of callsIn(text, commands)) {
    const red = command.params.findIndex((param, k) => param.name.toLowerCase() === "red" &&
      command.params[k + 1]?.name.toLowerCase() === "green" && command.params[k + 2]?.name.toLowerCase() === "blue");
    if (red < 0 || args.length < red + 3) continue;
    const parts = args.slice(red, red + 3).map((arg) => {
      const raw = text.slice(arg.from, arg.to);
      const lead = raw.length - raw.trimStart().length;
      const value = raw.trim();
      return /^\d{1,3}$/.test(value) && +value <= 255 ?
        { from: arg.from + lead, to: arg.from + lead + value.length, value: +value } : null;
    });
    if (parts.every(Boolean)) colours.push({ rgb: parts.map((part) => part.value), args: parts });
  }
  return colours;
}

const hex = (rgb) => "#" + rgb.map((value) => value.toString(16).padStart(2, "0")).join("");

class SwatchWidget extends WidgetType {
  constructor(rgb, open) {
    super();
    this.rgb = rgb;
    this.open = open;
  }
  eq(other) { return other.rgb.join() === this.rgb.join(); }
  toDOM(view) {
    const swatch = document.createElement("span");
    swatch.className = "b3d-swatch";
    swatch.style.background = `rgb(${this.rgb.join(",")})`;
    swatch.title = "Pick a colour";
    swatch.setAttribute("role", "button");
    swatch.setAttribute("aria-label", "Pick a colour");
    swatch.addEventListener("mousedown", (event) => {
      event.preventDefault();
      this.open(view, swatch);
    });
    return swatch;
  }
  ignoreEvent() { return true; }
}

// A swatch before each colour's red; picking a colour changes the numbers.
function colourSwatches(commands, onLiveEdit) {
  // One picker, beside the swatch it was opened from.
  let picker = null;
  const open = (view, swatch) => {
    const anchor = view.posAtDOM(swatch);
    // The colour, found again after each change: its red stays at anchor.
    const colourAt = () => {
      const line = view.state.doc.lineAt(anchor);
      const colour = coloursIn(line.text, commands).find((found) => line.from + found.args[0].from === anchor);
      return colour ? { line, colour } : null;
    };
    const found = colourAt();
    if (!found) return;
    if (!picker) {
      picker = document.createElement("input");
      picker.type = "color";
      picker.className = "b3d-colour-input";
      picker.setAttribute("aria-label", "Colour");
      document.body.append(picker);
    }
    const box = swatch.getBoundingClientRect();
    picker.style.left = `${box.left}px`;
    picker.style.top = `${box.bottom}px`;
    picker.value = hex(found.colour.rgb);
    const opened = found.colour.rgb;
    // The changes that make the colour rgb, or null when it is gone.
    const changesTo = (rgb) => {
      const now = colourAt();
      return now && now.colour.args.map((arg, k) => ({ from: now.line.from + arg.from, to: now.line.from + arg.to,
        insert: String(rgb[k]) }));
    };
    const chosen = () => [1, 3, 5].map((k) => parseInt(picker.value.slice(k, k + 2), 16));
    picker.oninput = () => {
      const changes = changesTo(chosen());
      if (!changes) return;
      view.dispatch({ changes, annotations: Transaction.addToHistory.of(false) });
      onLiveEdit();
    };
    // The picker closed: the colour chosen is one step to undo.
    picker.onchange = () => {
      const rgb = chosen();
      const back = changesTo(opened);
      if (back && rgb.join() !== opened.join()) recordOnce(view, back, () => changesTo(rgb), "input.colour");
    };
    try {
      picker.showPicker();
    } catch {
      picker.click();
    }
  };
  return ViewPlugin.fromClass(class {
    constructor(view) { this.decorations = this.build(view); }
    update(update) {
      if (update.docChanged || update.viewportChanged) this.decorations = this.build(update.view);
    }
    build(view) {
      const builder = new RangeSetBuilder();
      for (const { from, to } of view.visibleRanges) {
        for (let pos = from; pos <= to; ) {
          const line = view.state.doc.lineAt(pos);
          for (const colour of coloursIn(line.text, commands)) {
            builder.add(line.from + colour.args[0].from, line.from + colour.args[0].from,
              Decoration.widget({ widget: new SwatchWidget(colour.rgb, open), side: -1 }));
          }
          pos = line.to + 1;
        }
      }
      return builder.finish();
    }
  }, { decorations: (plugin) => plugin.decorations });
}

const theme = EditorView.baseTheme({
  ".b3d-swatch": { display: "inline-block", width: "0.8em", height: "0.8em", marginRight: "3px",
    verticalAlign: "-0.1em", border: "1px solid rgba(255,255,255,.55)", borderRadius: "2px", cursor: "pointer" },
  ".b3d-scrub-ready": { cursor: "ew-resize" },
});

// The tools, for a Blitz source's editor. commands is the runtime's
// (manifest.js).
export function liveTools({ commands, onLiveEdit }) {
  if (!document.getElementById("b3d-live-tools-style")) {
    // While a number is dragged, the pointer says so wherever it goes.
    const style = document.createElement("style");
    style.id = "b3d-live-tools-style";
    style.textContent = "body.b3d-scrubbing, body.b3d-scrubbing * { cursor: ew-resize !important; }\n" +
      ".b3d-colour-input { position: fixed; width: 1px; height: 1px; opacity: 0; border: 0; padding: 0; }";
    document.head.append(style);
  }
  return [scrubbing(onLiveEdit), colourSwatches(commands, onLiveEdit), theme];
}
